import { useAdminLibraryStore } from '@/store/adminLibrary.store';
import { useAdminTemplatesStore } from '@/store/adminTemplates.store';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import type { IAdminCategory } from '@/mocks/adminLibrary';

// Back-office mock-up: the classification lists the client edits on the categories page (ADM-03
// themes and sub-themes, ADM-05 / RES-03 resource categories, EDI-09 motif categories, RES-02
// graduation themes and graphic styles), and what each one counts.

type TAdminCategoryListKey = 'modeles' | 'motifs' | 'ressources' | 'styles-graduations' | 'thematiques-graduations';

interface IAdminCategoryListConfig {
    addLabel: string;
    addSubLabel: string;
    back: { label: string; to: string };
    countLabel: string;
    hasSubCategories: boolean;
    nouns: { many: string; one: string };
    subNouns: { many: string; one: string };
    title: string;
}

const LIST_CONFIGS: Record<TAdminCategoryListKey, IAdminCategoryListConfig> = {
    modeles: {
        title: 'Thématiques des modèles',
        addLabel: 'Ajouter une thématique',
        addSubLabel: 'Ajouter un sous-thème',
        back: { label: 'Modèles', to: '/admin/modeles' },
        countLabel: 'Modèles',
        hasSubCategories: true,
        nouns: { one: 'thématique', many: 'thématiques' },
        subNouns: { one: 'sous-thème', many: 'sous-thèmes' },
    },
    ressources: {
        title: 'Catégories des ressources graphiques',
        addLabel: 'Ajouter une catégorie',
        addSubLabel: '',
        back: { label: 'Ressources graphiques', to: '/admin/bibliotheque?onglet=ressources' },
        countLabel: 'Ressources',
        hasSubCategories: false,
        nouns: { one: 'catégorie', many: 'catégories' },
        subNouns: { one: '', many: '' },
    },
    motifs: {
        title: 'Catégories des motifs de fond',
        addLabel: 'Ajouter une catégorie',
        addSubLabel: '',
        back: { label: 'Motifs de fond', to: '/admin/bibliotheque?onglet=motifs' },
        countLabel: 'Motifs',
        hasSubCategories: false,
        nouns: { one: 'catégorie', many: 'catégories' },
        subNouns: { one: '', many: '' },
    },
    'thematiques-graduations': {
        title: 'Thématiques des graduations',
        addLabel: 'Ajouter une thématique',
        addSubLabel: '',
        back: { label: 'Graduations', to: '/admin/bibliotheque?onglet=graduations' },
        countLabel: 'Graduations',
        hasSubCategories: false,
        nouns: { one: 'thématique', many: 'thématiques' },
        subNouns: { one: '', many: '' },
    },
    'styles-graduations': {
        title: 'Styles graphiques des graduations',
        addLabel: 'Ajouter un style graphique',
        addSubLabel: '',
        back: { label: 'Graduations', to: '/admin/bibliotheque?onglet=graduations' },
        countLabel: 'Graduations',
        hasSubCategories: false,
        nouns: { one: 'style graphique', many: 'styles graphiques' },
        subNouns: { one: '', many: '' },
    },
};

function isAdminCategoryListKey(key: string): key is TAdminCategoryListKey {
    return key in LIST_CONFIGS;
}

function useAdminCategoryList(key: TAdminCategoryListKey) {
    const templatesStore = useAdminTemplatesStore();
    const libraryStore = useAdminLibraryStore();
    const { templates, themes } = storeToRefs(templatesStore);
    const { assets, classifications, graduations } = storeToRefs(libraryStore);

    const config = LIST_CONFIGS[key];

    const categories = computed<IAdminCategory[]>(() => {
        switch (key) {
            case 'modeles':
                return themes.value;
            case 'motifs':
                return classifications.value.patternCategories;
            case 'ressources':
                return classifications.value.resourceCategories;
            case 'thematiques-graduations':
                return classifications.value.graduationThemes;
            default:
                return classifications.value.graphicStyles;
        }
    });

    function count(id: string, subCategory?: string): number {
        switch (key) {
            case 'modeles':
                return templates.value.filter(
                    (template) => template.themeId === id && (subCategory === undefined || template.subCategory === subCategory),
                ).length;
            case 'motifs':
                return assets.value.filter((asset) => asset.kind === 'pattern' && asset.categoryId === id).length;
            case 'ressources':
                return assets.value.filter((asset) => asset.kind === 'resource' && asset.categoryId === id).length;
            case 'thematiques-graduations':
                return graduations.value.filter((graduation) => graduation.themeId === id).length;
            default:
                return graduations.value.filter((graduation) => graduation.graphicStyleId === id).length;
        }
    }

    // `renamed` maps, per theme, a sub-theme's former French label to its new one, so the designs
    // filed under it follow the rename.
    function save(next: IAdminCategory[], renamed: Record<string, Record<string, string>>): void {
        switch (key) {
            case 'modeles':
                templatesStore.saveThemes(next, renamed);
                return;
            case 'motifs':
                libraryStore.saveClassification('patternCategories', next);
                return;
            case 'ressources':
                libraryStore.saveClassification('resourceCategories', next);
                return;
            case 'thematiques-graduations':
                libraryStore.saveClassification('graduationThemes', next);
                return;
            default:
                libraryStore.saveClassification('graphicStyles', next);
        }
    }

    return { categories, config, count, save };
}

export { isAdminCategoryListKey, useAdminCategoryList };

export type { TAdminCategoryListKey };
